
import React from "react";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, ITournamentEdition, ITournamentEditionMatch, ITournamentEditionMatchResult, ITournamentEditionMatchResultProof, ITournamentEditionRegisteredParticipant, ITournamentEditionScoring, SanitizedResponse, TournamentEditionScoringType, TournamentMatchStatus, TournamentService } from "../../../../../services";
import { Elements } from "../../../../shared/Elements";
import Snapper from "../../../../shared/Snapper";
import { Schemes, Utils } from "../../../../../utils";
import { Table, DOMHelper, TableSelectionMode, Classname, Column, Checkbox, BoolHelper, Scheme, FormControl, TextAreaInput, ButtonManageRef, TextInput, DateHelper, Dialog, Button, TabPanel, NumberInput, TabPane, ScrollPanel, FileInput, FileInputMode, FileHelper, NoseurObject, NoseurButtonOnClickEvent, useScreenDimension } from "@rivtn/noseur";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import { CreateUpdateMatch } from "./shared/CreateUpdateMatch";
import { match } from "assert";
import Result, { getProofFileType } from "../../../tournaments/your_tournament/shared/Result";

interface MatchFixturesProps {
    historic?: boolean;
    showRecent?: boolean;
    tournamentEdition: ITournamentEdition;
    dataComponentState?: [React.ReactElement | undefined, React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>];
}

function MatchFixtures(props: MatchFixturesProps) {

    const { width } = useScreenDimension();
    const { tournamentEdition, showRecent, historic } = props;
    const tournamentService = TournamentService.getInstance();
    let { id: hostPublicId = "" } = ReactRouterDom.useParams();
    const labels = i18nManager.Labels.host.manage_tournament.match_fixtures;
    const [dataComponent, setDataComponent] = props.dataComponentState ?? [];
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [matchToSubmitResult, setMatchToSubmitResult] = React.useState<ITournamentEditionMatch>();
    let [tournamentCustomScorings, setTournamentCustomScorings] = React.useState<ITournamentEditionScoring[]>([]);
    const [viewType, setViewType] = React.useState<"LIST" | "CARD" | "CALENDAR">(searchParams.get("vt") as any ?? "LIST");
    const [tournamentMatches, setTournamentMatches] = React.useState<ApiResponseData<ITournamentEditionMatch> | undefined>({});
    const [submittedResultActiveParticipant, setSubmittedResultActiveParticipant] = React.useState<ITournamentEditionRegisteredParticipant>();

    React.useEffect(() => {
        tournamentService.queryTournamentEditionScorings(tournamentEdition.external_id!, {}).then(({ content }: SanitizedResponse<ITournamentEditionScoring>) => {
            tournamentCustomScorings = content;
            setTournamentCustomScorings(content);
            fetchInformation();
        }).catch(BaseService.reportError);

        return () => {
            setDataComponent?.(undefined);
        };
    }, []);

    React.useEffect(() => {
        Utils.updateSearchParams("vt", viewType, setSearchParams);
    }, [viewType]);

    return (<div className="mt-participants" style={showRecent ? { padding: 0 } : undefined}>
        {(!showRecent) && (<>
            <div className="mt-query-section">
                {(historic) && (<div className="l" style={{ display: "flex", alignItems: "center", marginTop: 15 }}>
                    <i className="fa fa-arrow-left" style={{ cursor: "pointer" }} onClick={() => window.history.back()} />
                    <span className="lined-limit" style={{ fontSize: 16, marginLeft: 10 }}>{labels.historic_matches}</span>
                </div>)}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 0, marginTop: 10, flex: 1, justifyContent: (historic ? "flex-end" : "flex-start") }}>
                    {historic && (<>
                        <span className="link" style={{ marginRight: 20, marginTop: 10 }}>{labels.export}</span>
                        <Snapper style={{ marginRight: 20, marginTop: 10 }} header={viewType === "CALENDAR" ? labels.calender_overview_snap : labels.matches_overview_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
                    </>)}
                    {Elements.buildConfirmDialogForm(labels.query_inputs.filter((q) => (width > 800 || q.name === "status")), (name, value) => {
                        Utils.updateSearchParams(name, value, setSearchParams, fetchInformation);
                    }, {}, undefined, { flex: 0.5, marginTop: 0, flexDirection: "row", alignItems: "center" }, undefined, {
                        marginRight: 15, marginTop: 0
                    })}
                </div>
                {(!historic) && (<div style={{ display: "flex" }}>
                    <span className="link" style={{ marginRight: 20 }} onClick={() => CreateUpdateMatch(tournamentEdition.external_id, fetchInformation)}>{labels.add_new_match}</span>
                    {viewType === "CALENDAR"
                        ? (<span className="link" style={{ marginRight: 20 }} onClick={() => setViewType("LIST")}>{labels.data_view}</span>)
                        : (<span className="link" style={{ marginRight: 20 }} onClick={() => setViewType("CALENDAR")}>{labels.calendar_view}</span>)}
                    <span className="link" style={{ marginRight: 20 }}>{labels.export}</span>
                    <Snapper header={viewType === "CALENDAR" ? labels.calender_overview_snap : labels.matches_overview_snap} template={[{ p: "tour_name", v: tournamentEdition.name }]} />
                </div>)}
            </div>
        </>)}
        {(BoolHelper.equalsAny(viewType, ["LIST", "CARD"])) && (<div className="mt-table-compound">
            {(viewType === "LIST") && (<Table stripedRows scheme={Schemes.OMNEHOST} data={tournamentMatches?.content} totalRecords={tournamentMatches?.total_elements} dataRefreshKeys={["external_id", "created_at", "status"]}
                style={{ flex: 1, width: "100%" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE}
                emptyState={Elements.buildEmptyListView("fa fa-users", (<span>
                    {labels.table.empty_label_parts.have_no}{" "}
                    <span className="link" onClick={() => CreateUpdateMatch(tournamentEdition.external_id, fetchInformation)}>{labels.table.empty_label_parts.add_new}</span>{" "}
                    {labels.table.empty_label_parts.or}{" "}
                    <ReactRouterDom.Link className="link" to={`/hosts/${hostPublicId}/tournament/${tournamentEdition.tournament?.external_id}/stage-format-bracket`}>{labels.table.empty_label_parts.update_stage}</ReactRouterDom.Link>{" "}
                </span>), { marginTop: 150 })} selectionMode={TableSelectionMode.SINGLE} rowSelection={true} valuedRowProps={(d) => {
                    return {
                        className: Classname.build("host-tour-row", { "active": (dataComponent as any)?.key === d?.external_id })
                    };
                }} onRowSelection={(tournamentMatch) => {
                    Utils.updateSearchParams("smatch", tournamentMatch.external_id, setSearchParams);
                    setDataComponent?.(buildTournamentMatchDetails(tournamentMatch));
                    return false;
                }}>
                <Column className="table-c" header={labels.table.headers.match} template={(v: ITournamentEditionMatch) => {
                    return (v.group_number ? `${labels.group} ${v.group_number} - ` : "") + (v.match_number ? `${labels.match} ${v.match_number}` : "");
                }} />
                <Column className="table-c" header={labels.table.headers.date} dataKey="start_date" template={(v) => Utils.formatToFineDate(v, true)} />
                <Column className="table-c" header={labels.table.headers.participants} dataKey="tournament_edition_registered_participants" template={(v: ITournamentEditionRegisteredParticipant[]) => {
                    return (<div style={{ display: "flex" }}>
                        {v.slice(0, 5).map((p) => {
                            const name = Utils.getResourceName(p.resource);
                            const imageUrl = Utils.getResourceImage(p.resource);
                            return (<img key={name} alt={name} src={imageUrl} style={{ width: 20, height: 20, borderRadius: "100%", marginRight: 10 }} />);
                        })}
                    </div>);
                }} />
                <Column className="table-c" doNotRender={width < 800 || !!dataComponent} header={labels.table.headers.location} dataKey="location" />
                <Column className="table-c" doNotRender={!!dataComponent} header={labels.table.headers.status} dataKey="status" template={(v) => buildStatusLabel(v)} />
                <Column className="table-c" doNotRender={!!dataComponent} template={(v) => (<div style={{ display: "flex", alignItems: "center" }}>
                    {(!showRecent) && (<Snapper header={labels.match_overview_snap} style={{ marginRight: 20 }}
                        template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "match", v: (v.group_number ? `${labels.group} ${v.group_number} - ` : "") + (v.match_number ? `${labels.match} ${v.match_number}` : "") }]} />)}
                    <i className="fa fa-angle-right" />
                </div>)} />
            </Table>)}
            {(!showRecent) && (<div className="pagination">
                {Elements.buildPaginator(tournamentMatches ?? {},
                    ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchInformation),
                    parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
                {(width > 800) && (<div style={{ display: "flex" }}>
                    <Checkbox scheme={Schemes.OMNEHOST} style={{ marginRight: 15, marginTop: 20 }} label={labels.card_view} defaultChecked={viewType === "CARD"} onChecked={(v) => {
                        setViewType(v ? "CARD" : "LIST");
                    }} />
                    {Elements.buildSorter(labels.table.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchInformation), labels.table.sorts[0].label)}
                </div>)}
            </div>)}
        </div>)}

        <Dialog maximized notClosable visible={!!matchToSubmitResult} contentProps={{ className: "cd-c" }}
            footer={<div className="noseur-alert-footer noseur-alert-footer-right">
                <Button textOnly scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.close} style={{ fontWeight: "bold" }} onClick={() => {
                    setMatchToSubmitResult(undefined);
                    setSubmittedResultActiveParticipant(undefined);
                }} />
                <Button scheme={Schemes.OMNEHOST} text={labels.dialog.record_match_results.update_result} style={{ marginLeft: 40, padding: "10px 40px", minWidth: 100 }} loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={submitMatchRecord} />
            </div>}>
            <span className="title">{labels.dialog.record_match_results.title}</span>
            <span className="desc" dangerouslySetInnerHTML={{
                __html: labels.dialog.record_match_results.desc.replaceAll("${group_number}", (matchToSubmitResult?.group_number + ""))
                    .replaceAll("${match_number}", (matchToSubmitResult?.match_number + ""))
            }} />
            <TabPane scheme={Schemes.OMNEHOST} idleScheme={Schemes.IDLE} style={{ borderTop: "1px solid var(--noseurSchemeBorderColor, #EEF0F8)", marginTop: 20, flex: 1 }}
                attrsRelay={{ content: { style: { margin: 0, padding: 0, border: "none", borderRadius: 0 } } }}>
                {tournamentCustomScorings.map((tournamentScore) => {
                    const name = (tournamentScore.type === TournamentEditionScoringType.TOURNAMENT_SCORE ? "# " + i18nManager.Labels.host.manage_tournament.scoring.detail.tournament_score : tournamentScore.name);

                    return (<TabPanel key={tournamentScore.external_id} header={<>{name}{(tournamentScore.type === TournamentEditionScoringType.TOURNAMENT_SCORE) && (<> <div style={{ width: 5, height: 5, backgroundColor: "#FF7171", margin: "auto", marginLeft: 10 }} /></>)}</>}>
                        <div className="mt-submit-result">
                            <TabPane activeIndex={1} scheme={Schemes.OMNEHOST} idleScheme={Schemes.IDLE} style={{ marginTop: 5 }} attrsRelay={{ content: { style: { margin: 0, padding: 0, border: "none", borderRadius: 0 } } }}>
                                <TabPanel header={labels.dialog.record_match_results.submitted_results} className="mt-submitted-results">
                                    <div className="mt-s-results-p-list">
                                        {matchToSubmitResult?.tournament_edition_registered_participants?.map((participant) => (
                                            <div className={submittedResultActiveParticipant?.external_id === participant.external_id ? "active" : undefined} onClick={() => setSubmittedResultActiveParticipant(participant)}>
                                                <img alt={participant.external_id} src={Utils.getResourceImage(participant.resource)} />
                                            </div>))}
                                    </div>
                                    <div className="mt-s-results-sr-list">
                                        <div style={{ margin: 15 }} dangerouslySetInnerHTML={{
                                            __html:
                                                labels.dialog.record_match_results.submitted_results_and_proofs.replace("${name}", Utils.getResourceName(submittedResultActiveParticipant?.resource))
                                        }} />
                                        <ScrollPanel className="ma-results">
                                            <div>
                                                {Object.entries(matchToSubmitResult?.tournament_edition_registered_participant_submitted_results?.[submittedResultActiveParticipant?.external_id ?? 0] ?? {}).reduce((acc, [scoringId, submittedResults]) => {
                                                    submittedResults.forEach((t) => {
                                                        t.submitter_id = submittedResultActiveParticipant?.external_id ?? "0";
                                                        t.tournament_edition_registered_participants = matchToSubmitResult?.tournament_edition_registered_participants;
                                                        t.tournament_scoring = tournamentCustomScorings?.find((t) => t.external_id === scoringId) ?? { external_id: scoringId } as any;
                                                    })
                                                    return acc.concat(submittedResults);
                                                }, [] as ITournamentEditionMatchResult[]).reverse().map((entry) => (<Result key={entry.date} pid={submittedResultActiveParticipant?.external_id} result={entry}
                                                    isManaging style={{ padding: 15, marginTop: 0, borderTop: "1px solid var(--noseurSchemeBorderColor, #EEF0F8)" }} onAccept={(proof) => {
                                                        const acceptedProofs = matchToSubmitResult?.final_tournament_proofs?.[tournamentScore.external_id] ?? [];
                                                        acceptedProofs.push(proof);
                                                        setMatchToSubmitResult({ ...matchToSubmitResult!, final_tournament_proofs: { ...matchToSubmitResult?.final_tournament_proofs, [tournamentScore.external_id]: acceptedProofs } });
                                                    }} acceptedProofs={(matchToSubmitResult?.final_tournament_proofs?.[tournamentScore.external_id] ?? []).map((p) => p.location_url)} />))}
                                            </div>
                                        </ScrollPanel>
                                    </div>
                                </TabPanel>
                                <TabPanel header={labels.dialog.record_match_results.actual_result_scoring}>
                                    <Table stripedRows scheme={Schemes.OMNEHOST} data={matchToSubmitResult?.tournament_edition_registered_participants} totalRecords={matchToSubmitResult?.tournament_edition_registered_participants.length} dataRefreshKeys={["external_id", "created_at"]}
                                        style={{ flex: 1, width: "100%", marginTop: 0, overflow: "auto" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE} >
                                        <Column className="table-c" header={"#"} template={(v) => {
                                            const name = Utils.getResourceName(v.resource);
                                            const imageUrl = Utils.getResourceImage(v.resource);

                                            return (<div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                                                <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                                                <span>{name}</span>
                                            </div>);
                                        }} />
                                        {(!!matchToSubmitResult && Object.keys(tournamentScore.scoring ?? {}).length > 0) ? (<React.Fragment key={DOMHelper.uniqueElementId("tslbo")}>
                                            {Object.values(tournamentScore.scoring ?? {})?.map((scoreSetting) => (<Column key={scoreSetting.id} className="table-c" header={`${scoreSetting.name} ${scoreSetting.submission_name ? "(" + scoreSetting.submission_name + ")" : ""}`} template={(participant: ITournamentEditionRegisteredParticipant) => {
                                                let scoreValue = 0;
                                                if (!(tournamentScore.external_id in matchToSubmitResult?.scores!)) {
                                                    matchToSubmitResult!.scores[tournamentScore.external_id] = {};
                                                }
                                                if (!(participant.external_id in matchToSubmitResult!.scores[tournamentScore.external_id])) {
                                                    matchToSubmitResult!.scores[tournamentScore.external_id][participant.external_id] = {};
                                                }
                                                if (participant.external_id in matchToSubmitResult?.tournament_edition_registered_participant_losers!) {
                                                    scoreValue = scoreSetting.score_per_loss;
                                                } else if (participant.external_id in matchToSubmitResult?.tournament_edition_registered_participant_drawers!) {
                                                    scoreValue = scoreSetting.score_per_draw;
                                                } else if (participant.external_id in matchToSubmitResult?.tournament_edition_registered_participant_winners!) {
                                                    scoreValue = scoreSetting.score_per_win;
                                                }
                                                return (<>
                                                    <NumberInput style={{ maxWidth: 150 }} scheme={Schemes.OMNEHOST} defaultValue={matchToSubmitResult?.scores[tournamentScore.external_id]?.[participant.external_id]?.[scoreSetting.id]} onInputComplete={(value) => {
                                                        if (!value) value = "0";
                                                        matchToSubmitResult!.scores[tournamentScore.external_id][participant.external_id][scoreSetting.id] = parseInt(value);
                                                        updateMatchSubmissionRecords(matchToSubmitResult!, tournamentScore.external_id);
                                                    }} />
                                                    {(BoolHelper.equalsAny(scoreSetting.score_system, ["POINTS"])) && (<span style={{ opacity: 0.5, margin: "0px 10px" }}>({scoreValue})</span>)}
                                                </>);
                                            }} />))}
                                        </React.Fragment>) : <Column />}
                                    </Table>
                                </TabPanel>
                            </TabPane>
                            <div className="mt-accepted-proofs">
                                <b>{labels.dialog.record_match_results.accepted_proofs}</b>
                                <Result isManaging isAccepted result={{
                                    proofs: (matchToSubmitResult?.final_tournament_proofs?.[tournamentScore.external_id] ?? [])
                                } as ITournamentEditionMatchResult} onAcceptRemove={(proof) => {
                                    const acceptedProofs = matchToSubmitResult?.final_tournament_proofs?.[tournamentScore.external_id] ?? [];
                                    const index = acceptedProofs.findIndex((p) => p.location_url === proof.location_url);
                                    if (index > -1) {
                                        acceptedProofs.splice(index, 1);
                                        setMatchToSubmitResult({ ...matchToSubmitResult!, final_tournament_proofs: { ...matchToSubmitResult?.final_tournament_proofs, [tournamentScore.external_id]: acceptedProofs } });
                                    }
                                }} />
                                <FileInput multiple scheme={Schemes.OMNEHOST} mode={FileInputMode.BUTTON} style={{ marginTop: 10 }} buttonProps={{
                                    textOnly: true,
                                    borderless: true,
                                    fillOnHover: false,
                                    style: { paddingLeft: 0, fontWeight: "bold" },
                                    text: labels.dialog.record_match_results.upload_proofs,
                                }} onSelectFiles={(files) => {
                                    const acceptedProofs = matchToSubmitResult?.final_tournament_proofs?.[tournamentScore.external_id] ?? [];
                                    files.forEach((file) => {
                                        acceptedProofs.push({
                                            __file__: file,
                                            name: file.name,
                                            size: file.size,
                                            type: getProofFileType(file.type),
                                            location_url: URL.createObjectURL(file),
                                        });
                                    });
                                    setMatchToSubmitResult({ ...matchToSubmitResult!, final_tournament_proofs: { ...matchToSubmitResult?.final_tournament_proofs, [tournamentScore.external_id]: acceptedProofs } });
                                }} />
                            </div>
                            <div className="mt-winners-losers-drawers">
                                <div className="mt-wld-section">
                                    <div className="t">
                                        {labels.dialog.record_match_results.winners}
                                    </div>
                                    <div className="ps">
                                        {Object.keys(matchToSubmitResult?.tournament_edition_registered_participant_winners ?? {})?.map((p) => matchToSubmitResult?.tournament_edition_registered_participants?.find((r) => r.external_id === p))?.map((v) => {
                                            if (!v) return undefined;
                                            const name = Utils.getResourceName(v.resource);
                                            const imageUrl = Utils.getResourceImage(v.resource);

                                            return (<div key={v.external_id} style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                                                <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                                                <span>{name}</span>
                                            </div>);
                                        })}
                                    </div>
                                </div>
                                <div className="mt-wld-section">
                                    <div className="t">
                                        {labels.dialog.record_match_results.losers}
                                    </div>
                                    <div className="ps">
                                        {Object.keys(matchToSubmitResult?.tournament_edition_registered_participant_losers ?? {})?.map((p) => matchToSubmitResult?.tournament_edition_registered_participants?.find((r) => r.external_id === p))?.map((v) => {
                                            if (!v) return undefined;
                                            const name = Utils.getResourceName(v.resource);
                                            const imageUrl = Utils.getResourceImage(v.resource);

                                            return (<div key={v.external_id} style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                                                <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                                                <span>{name}</span>
                                            </div>);
                                        })}
                                    </div>
                                </div>
                                <div className="mt-wld-section">
                                    <div className="t">
                                        {labels.dialog.record_match_results.drawers}
                                    </div>
                                    <div className="ps">
                                        {Object.keys(matchToSubmitResult?.tournament_edition_registered_participant_drawers ?? {})?.map((p) => matchToSubmitResult?.tournament_edition_registered_participants?.find((r) => r.external_id === p))?.map((v) => {
                                            if (!v) return undefined;
                                            const name = Utils.getResourceName(v.resource);
                                            const imageUrl = Utils.getResourceImage(v.resource);

                                            return (<div key={v.external_id} style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                                                <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                                                <span>{name}</span>
                                            </div>);
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabPanel>);
                })}
            </TabPane>
        </Dialog>
    </div>);

    function fetchInformation() {
        setTournamentMatches({});
        setDataComponent?.(undefined);
        tournamentService.queryTournamentEditionMatches(tournamentEdition.external_id!, {
            page: parseInt(searchParams.get("page") ?? "1"),
            size: parseInt(searchParams.get("size") ?? "100"),
        }).then(({ apiResponse }: SanitizedResponse<ITournamentEditionMatch>) => {
            setTournamentMatches(apiResponse.data!);
            for (const tournamentEditionMatch of (apiResponse.data!.content ?? [])) {
                if (tournamentEditionMatch.external_id === searchParams.get("smatch")) {
                    setDataComponent?.(buildTournamentMatchDetails(tournamentEditionMatch));
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

    function buildStatusLabel(status: TournamentMatchStatus) {
        const color = {
            [TournamentMatchStatus.ENDED]: "#1D09D3",
            [TournamentMatchStatus.ONGOING]: "#09B1D3",
            [TournamentMatchStatus.UPCOMING]: "#D39A09",
            [TournamentMatchStatus.CANCELLED]: "#000000",
            [TournamentMatchStatus.FORFEITED]: "#000000",
        }[status];
        const label = labels.query_inputs.find((i) => i.name === "status")?.options?.find((o) => o.value === status)?.label ?? status;
        return (<span style={{ fontWeight: "bold", color }}>{Utils.toSaneSentenceFormat(label)}</span>);
    }

    function buildTournamentMatchDetails(tournamentMatch: ITournamentEditionMatch) {
        const tournamentScore = tournamentCustomScorings?.find((t) => t.type === TournamentEditionScoringType.TOURNAMENT_SCORE);
        const groupMatchLabel = (tournamentMatch.group_number ? `${labels.group} ${tournamentMatch.group_number} - ` : "") + (tournamentMatch.match_number ? `${labels.match} ${tournamentMatch.match_number}` : "");

        return (<div key={tournamentMatch.external_id} className="mt-participant-data">
            <div className="mt-participant-data-hctrl">
                <Snapper header={labels.match_overview_snap} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "match", v: groupMatchLabel }]} />
                <i className="fa fa-times close" onClick={() => {
                    setDataComponent?.(undefined);
                    Utils.updateSearchParams("smatch", null, setSearchParams);
                }} />
            </div>
            <div className="mt-participant-data-details">
                <span className="n">{groupMatchLabel}</span>
            </div>
            <div className="mt-participant-data-info">
                <div>
                    <span className="k">{labels.detail.status}</span>
                    <span className="v">{buildStatusLabel(tournamentMatch.status)}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.start_date}</span>
                    <span className="v">{Utils.formatToFineDate(tournamentMatch.start_date ?? "", true)}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.end_date}</span>
                    <span className="v">{Utils.formatToFineDate(tournamentMatch.end_date ?? "", true)}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.duration}</span>
                    <span className="v">{DateHelper.formatDuration(new Date(tournamentMatch.start_date), new Date(tournamentMatch.end_date))}</span>
                </div>
                <div>
                    <span className="k">{labels.detail.location}</span>
                    <span className="v">{tournamentMatch.location}</span>
                </div>
            </div>
            {(tournamentMatch.status === TournamentMatchStatus.CANCELLED) && (<div className="mt-participant-data-info">
                <span>
                    <span style={{ opacity: 0.6 }}>{labels.detail.cancellation_reason} </span>{tournamentMatch.cancel_or_forfeit_reason}
                </span>
            </div>)}
            <div className="mt-participant-data-actions" style={{ borderBottom: "unset" }}>
                <span className="link danger" onClick={() => deleteMatch(tournamentMatch)}>{labels.detail.delete}</span>
                <span onClick={() => cancelMatch(tournamentMatch)}
                    className={Classname.build("link danger", { "noseur-disabled": (BoolHelper.equalsAny(tournamentMatch.status, [TournamentMatchStatus.ONGOING, TournamentMatchStatus.ENDED, TournamentMatchStatus.CANCELLED])) })}>{labels.detail.cancel}</span>
                <span style={{ color: "#15B381" }} onClick={() => endMatch(tournamentMatch)}
                    className={Classname.build("link", { "noseur-disabled": (!BoolHelper.equalsAny(tournamentMatch.status, [TournamentMatchStatus.ONGOING])) })}>{labels.detail.end_match}</span>
                <span onClick={() => CreateUpdateMatch(tournamentEdition.external_id, fetchInformation, tournamentMatch)}
                    className={Classname.build("link", { "noseur-disabled": (BoolHelper.equalsAny(tournamentMatch.status, [TournamentMatchStatus.ONGOING, TournamentMatchStatus.ENDED, TournamentMatchStatus.CANCELLED])) })}>{labels.detail.edit_match}</span>
                <span
                    className={Classname.build("link", { "noseur-disabled": (BoolHelper.equalsAny(tournamentMatch.status, [TournamentMatchStatus.UPCOMING, TournamentMatchStatus.CANCELLED])) })} onClick={() => {
                        setSubmittedResultActiveParticipant(tournamentMatch.tournament_edition_registered_participants[0]);
                        setMatchToSubmitResult(tournamentMatch);
                    }}>{labels.detail.record_match}</span>
            </div>
            <Table stripedRows scheme={Schemes.OMNEHOST} data={tournamentMatch.tournament_edition_registered_participants} totalRecords={tournamentMatch.tournament_edition_registered_participants.length} dataRefreshKeys={["external_id", "created_at", "resource_id"]}
                style={{ flex: 1, width: "100%", marginTop: 0 }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE} >
                <Column className="table-c" doNotRender={!!dataComponent} header={labels.detail.table.headers.participant} template={(v) => {
                    const name = Utils.getResourceName(v.resource);
                    const imageUrl = Utils.getResourceImage(v.resource);

                    return (<div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                        <Snapper header={labels.detail.match_participant_snap} template={[{ p: "tour_name", v: tournamentEdition.name }, { p: "participant_name", v: name }]} />
                        <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                        <span>{name}</span>
                    </div>);
                }} />
                {(Object.keys(tournamentScore?.scoring ?? {}).length > 0) ? (<React.Fragment key={DOMHelper.uniqueElementId("tslbo")}>
                    {Object.values(tournamentScore?.scoring ?? {}).map((scoreSetting) => (<Column key={scoreSetting.id} className="table-c" header={scoreSetting.name} template={(v) => {
                        let score = 0;
                        if (BoolHelper.equalsAny(scoreSetting.score_system, ["POINTS"])) {
                            if (Object.keys(tournamentMatch.tournament_edition_registered_participant_losers).includes(v.external_id)) score = scoreSetting.score_per_loss;
                            if (Object.keys(tournamentMatch.tournament_edition_registered_participant_winners).includes(v.external_id)) score = scoreSetting.score_per_win;
                            if (Object.keys(tournamentMatch.tournament_edition_registered_participant_drawers).includes(v.external_id)) score = scoreSetting.score_per_draw;
                        } else {
                            score = tournamentMatch.scores?.[tournamentScore?.external_id ?? ""]?.[v.external_id]?.[scoreSetting.id];
                        }
                        return (<span key={scoreSetting.id} style={{ marginTop: 5, width: "max-content" }}>{score} {i18nManager.Labels.tournaments.your_tournament.home.scoring_system_map[scoreSetting.score_system.toLowerCase()]}</span>);
                    }} />))}
                </React.Fragment>) : <Column />}
            </Table>
        </div>);
    }

    function deleteMatch(tournamentMatch: ITournamentEditionMatch) {
        let tournamentName = "";
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replaceAll("${group_number}", (tournamentMatch.group_number + ""))
                .replaceAll("${match_number}", (tournamentMatch.match_number + "")),
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.how_it_affects.location} target="_blank">{dialogLabels.how_it_affects.label}</a>
                <a href={dialogLabels.alternative.location} target="_blank">{dialogLabels.alternative.label}</a>
                <span style={{ marginTop: 15 }}>{i18nManager.Labels.tournaments.dialog.confirm_tournament.to_continue}</span>
                <FormControl isFieldset fill label={i18nManager.Labels.tournaments.dialog.confirm_tournament.tour_name} style={{ marginTop: 15 }}>
                    <TextInput scheme={Schemes.OMNEHOST} style={{ width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        tournamentName = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                if (tournamentName !== tournamentEdition.tournament.name) {
                    dialogErrorRef.current!.innerText = i18nManager.Labels.tournaments.dialog.confirm_tournament.name_does_not_match;
                    return false;
                }
                button?.setLoadingState(true);
                tournamentService.deleteTournamentEditionMatch(tournamentEdition.external_id, tournamentMatch.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        setDataComponent?.(undefined);
                        Siren.alert(dialogLabels.successfully_deleted);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function endMatch(tournamentMatch: ITournamentEditionMatch) {
        const dialogLabels = labels.dialog.end_match;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replaceAll("${group_number}", (tournamentMatch.group_number + ""))
                .replaceAll("${match_number}", (tournamentMatch.match_number + "")),
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.endTournamentEditionMatch(tournamentEdition.external_id, tournamentMatch.external_id).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        Siren.alert(dialogLabels.successfully_ended);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function cancelMatch(tournamentMatch: ITournamentEditionMatch) {
        let reason = "";
        const dialogLabels = labels.dialog.cancel;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replaceAll("${group_number}", (tournamentMatch.group_number + ""))
                .replaceAll("${match_number}", (tournamentMatch.match_number + "")),
            content: (<div style={{ marginTop: 10 }}>
                <FormControl isFieldset fill label={dialogLabels.reason} style={{ marginTop: 15 }}>
                    <TextAreaInput scheme={Schemes.OMNEHOST} style={{ height: 150, width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        reason = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.cancelTournamentEditionMatch(tournamentEdition.external_id, tournamentMatch.external_id, reason).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        fetchInformation();
                        Siren.alert(dialogLabels.cancel_successful);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function updateMatchSubmissionRecords(matchToSubmitResult: ITournamentEditionMatch, scoringId: string) {
        const aggregatedScores: NoseurObject<number> = Object.entries(matchToSubmitResult!.scores[scoringId]).reduce((acc, [participantId, scores]) => {
            if (!(participantId in acc)) acc[participantId] = 0;
            acc[participantId] = Object.values(scores).reduce((acc, v) => acc + v, 0)
            return acc;
        }, {} as NoseurObject<number>);
        const aggregatedScoresKeys = Object.keys(aggregatedScores);
        const aggregatedScoresValues = Object.values(aggregatedScores);
        const isDraw = aggregatedScoresValues.every((val) => val === aggregatedScoresValues[0]);
        const highestParticipantId = aggregatedScoresKeys.reduce((a, b) => aggregatedScores[a] > aggregatedScores[b] ? a : b);
        const drawerIds = (isDraw ? aggregatedScoresKeys : []);
        let winnerIds = (!isDraw ? aggregatedScoresKeys.filter((id) => id === highestParticipantId) : []);
        let losersIds = (!isDraw ? aggregatedScoresKeys.filter((id) => id !== highestParticipantId) : []);
        if (aggregatedScoresKeys.length > 2) {
            winnerIds = winnerIds.concat(losersIds);
            losersIds = [];
        }
        matchToSubmitResult.tournament_edition_registered_participant_losers = {};
        matchToSubmitResult.tournament_edition_registered_participant_drawers = {};
        matchToSubmitResult.tournament_edition_registered_participant_winners = {};
        losersIds.forEach((participantId) => {
            if (!(participantId in matchToSubmitResult.tournament_edition_registered_participant_losers)) {
                matchToSubmitResult.tournament_edition_registered_participant_losers[participantId] = {};
            }
            matchToSubmitResult.tournament_edition_registered_participant_losers[participantId][scoringId] = matchToSubmitResult!.scores[scoringId][participantId];
        });
        drawerIds.forEach((participantId) => {
            if (!(participantId in matchToSubmitResult.tournament_edition_registered_participant_drawers)) {
                matchToSubmitResult.tournament_edition_registered_participant_drawers[participantId] = {};
            }
            matchToSubmitResult.tournament_edition_registered_participant_drawers[participantId][scoringId] = matchToSubmitResult!.scores[scoringId][participantId];
        });
        winnerIds.forEach((participantId) => {
            if (!(participantId in matchToSubmitResult.tournament_edition_registered_participant_winners)) {
                matchToSubmitResult.tournament_edition_registered_participant_winners[participantId] = {};
            }
            matchToSubmitResult.tournament_edition_registered_participant_winners[participantId][scoringId] = matchToSubmitResult!.scores[scoringId][participantId];
        });
        setMatchToSubmitResult({ ...matchToSubmitResult });
    }

    function submitMatchRecord(e: NoseurButtonOnClickEvent) {
        if (!matchToSubmitResult) return;
        e.manageRef?.setLoadingState(true);
        const payload = {
            scores: matchToSubmitResult.scores,
            proofs: matchToSubmitResult.final_tournament_proofs,
            tournament_edition_registered_participant_losers: Object.keys(matchToSubmitResult.tournament_edition_registered_participant_losers),
            tournament_edition_registered_participant_drawers: Object.keys(matchToSubmitResult.tournament_edition_registered_participant_drawers),
            tournament_edition_registered_participant_winners: Object.keys(matchToSubmitResult.tournament_edition_registered_participant_winners),
        };
        tournamentService.scoreTournamentEditionMatchResult(tournamentEdition.external_id!, matchToSubmitResult.external_id, payload).then(({ apiResponse }: any) => {
            Siren.alert(labels.dialog.record_match_results.match_result_updated_successfully);
        }).catch(BaseService.reportError).finally(() => e.manageRef?.setLoadingState(false));
    }

}

export default MatchFixtures;

